import { c as _c } from "react/compiler-runtime";
import { RefObject, useEffect, useRef } from 'react';
import type { ItemListEventItem } from '@spa/lib/tracking/eCommerceTrackingEventSchema';
import { tracker } from '@spa/lib/tracking/tracker';
const PRODUCT_TILE_VISIBILITY_THRESHOLD = 0.5;
const PRODUCT_TILE_DWELL_TIME_MS = 500;
interface UseProductListTrackingParams {
  readonly anonymousId: string;
  readonly authenticatedId: string | null;
  readonly listId: string;
  readonly listName: string;
  readonly productsCount: number;
  readonly productListRef: RefObject<HTMLDivElement | null>;
  readonly viewItemListEventItems: ReadonlyArray<ItemListEventItem>;
}
export function useProductListTracking(t0) {
  const $ = _c(14);
  const {
    anonymousId,
    authenticatedId,
    listId,
    listName,
    productListRef,
    productsCount,
    viewItemListEventItems
  } = t0;
  let t1;
  if ($[0] !== anonymousId || $[1] !== authenticatedId) {
    t1 = {
      anonymousId,
      authenticatedId
    };
    $[0] = anonymousId;
    $[1] = authenticatedId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const identityRef = useRef(t1);
  let t2;
  let t3;
  if ($[3] !== anonymousId || $[4] !== authenticatedId) {
    t2 = () => {
      identityRef.current = {
        anonymousId,
        authenticatedId
      };
    };
    t3 = [anonymousId, authenticatedId];
    $[3] = anonymousId;
    $[4] = authenticatedId;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== listId || $[8] !== listName || $[9] !== productListRef || $[10] !== productsCount || $[11] !== viewItemListEventItems) {
    t4 = () => {
      if (productListRef.current && productsCount > 0) {
        const tiles = Array.from(productListRef.current.querySelectorAll("[data-product-index]"));
        const tileElementsByIndex = new Map();
        const trackedItemIndices = new Set();
        const dwellTimeoutIds = new Map();
        const dwellQualifiedItemIndices = new Set();
        const visibleItemIndices = new Set();
        let animationFrameId = null;
        tiles.forEach(tile => {
          const productIndex = Number(tile.dataset.productIndex);
          if (Number.isInteger(productIndex)) {
            tileElementsByIndex.set(productIndex, tile);
          }
        });
        const trackVisibleTiles = () => {
          animationFrameId = null;
          const sortedVisibleIndices = Array.from(visibleItemIndices).filter(index => dwellQualifiedItemIndices.has(index) && !trackedItemIndices.has(index)).sort(_temp);
          if (sortedVisibleIndices.length !== 0) {
            sortedVisibleIndices.forEach(index_0 => {
              trackedItemIndices.add(index_0);
              dwellQualifiedItemIndices.delete(index_0);
            });
            const currentIdentity = identityRef.current;
            tracker.viewItemList({
              ...currentIdentity,
              ecommerce: {
                item_list_id: listId,
                item_list_name: listName,
                items: sortedVisibleIndices.map(index_1 => viewItemListEventItems[index_1]).filter(_temp2)
              }
            });
          }
        };
        const scheduleVisibleTileTracking = () => {
          if (animationFrameId === null) {
            animationFrameId = window.requestAnimationFrame(trackVisibleTiles);
          }
        };
        const clearDwellTimeout = productIndex_0 => {
          const timeoutId = dwellTimeoutIds.get(productIndex_0);
          if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
            dwellTimeoutIds.delete(productIndex_0);
          }
        };
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const tile_0 = entry.target as HTMLElement;
            const productIndex_1 = Number(tile_0.dataset.productIndex);
            if (Number.isInteger(productIndex_1)) {
              const isVisible = entry.isIntersecting && entry.intersectionRatio >= PRODUCT_TILE_VISIBILITY_THRESHOLD;
              if (isVisible) {
                visibleItemIndices.add(productIndex_1);
                if (!trackedItemIndices.has(productIndex_1) && !dwellTimeoutIds.has(productIndex_1)) {
                  const timeoutId_0 = window.setTimeout(() => {
                    dwellTimeoutIds.delete(productIndex_1);
                    const isStillVisible = visibleItemIndices.has(productIndex_1) && tileElementsByIndex.has(productIndex_1);
                    if (isStillVisible && !trackedItemIndices.has(productIndex_1)) {
                      dwellQualifiedItemIndices.add(productIndex_1);
                    }
                    if (dwellTimeoutIds.size === 0) {
                      scheduleVisibleTileTracking();
                    }
                  }, PRODUCT_TILE_DWELL_TIME_MS);
                  dwellTimeoutIds.set(productIndex_1, timeoutId_0);
                }
              } else {
                visibleItemIndices.delete(productIndex_1);
                dwellQualifiedItemIndices.delete(productIndex_1);
                clearDwellTimeout(productIndex_1);
                if (dwellQualifiedItemIndices.size > 0) {
                  scheduleVisibleTileTracking();
                }
              }
            }
          });
        }, {
          threshold: PRODUCT_TILE_VISIBILITY_THRESHOLD
        });
        tiles.forEach(tile_1 => {
          observer.observe(tile_1);
        });
        return () => {
          dwellTimeoutIds.forEach(_temp3);
          observer.disconnect();
          if (animationFrameId !== null) {
            window.cancelAnimationFrame(animationFrameId);
          }
        };
      }
    };
    t5 = [listId, productsCount, listName, viewItemListEventItems, productListRef];
    $[7] = listId;
    $[8] = listName;
    $[9] = productListRef;
    $[10] = productsCount;
    $[11] = viewItemListEventItems;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
}
function _temp3(timeoutId_1) {
  window.clearTimeout(timeoutId_1);
}
function _temp2(item) {
  return Boolean(item);
}
function _temp(a, b) {
  return a - b;
}